import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngxs/store';
import { Observable, timer } from 'rxjs';
import { EditMultipleSelectDialogData } from 'src/app/interfaces/edit-multiple-select-dialog-data';
import { Tag, UpdateVersionRequestModel, Version } from 'src/app/services/api';
import { ProductApiStatusState, ProductState, PutVersion } from '../../product/state';
import { SubscriptionHandlerComponent } from '../subscription-handler/subscription-handler.component';

@Component({
  selector: 'app-edit-multiple-select-dialog',
  imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule, MatInputModule, MatCardModule, FormsModule, MatOptionModule, MatSelectModule, MatDatepickerModule, MatFormFieldModule, ReactiveFormsModule, MatTabsModule, MatTooltipModule, MatProgressSpinnerModule],
  templateUrl: './edit-multiple-select-dialog.component.html',
  styleUrl: './edit-multiple-select-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditMultipleSelectDialogComponent extends SubscriptionHandlerComponent implements OnInit
{
  editedValue$: Observable<boolean | null> = this.store.select(ProductApiStatusState.editedValue);
  productTags$: Observable<Tag[] | undefined> = this.store.select(ProductState.tags);

  // Signal to track the currently selected tag IDs
  readonly values = signal([] as number[]);

  // FormControl for the UI select component
  selectedProductTags = new FormControl<Tag[]>([]);

  version: Version;

  // Edit button pressed flag
  editPressed: boolean = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: EditMultipleSelectDialogData,
    private store: Store,
    private dialogRef: MatDialogRef<EditMultipleSelectDialogComponent>)
  {
    super();

    // Initialize the values signal with the provided values from data
    this.values.set(data.values);
  }

  ngOnInit(): void
  {
    // Subscribe to get all available tags
    this.subscribeTo(this.productTags$, allTags =>
    {
      if (allTags)
      {
        // Find the Tag objects that match our IDs in values
        const selectedTags = allTags.filter(tag =>
        {
          const isIncluded = this.values().includes(tag.id!);
          return isIncluded;
        });

        this.selectedProductTags.setValue(selectedTags);

        // Subscribe to changes in the FormControl
        this.subscribeTo(this.selectedProductTags.valueChanges, selectedTags =>
        {
          if (selectedTags)
          {
            // Extract IDs from the selected Tag objects and update values signal
            const selectedIds = selectedTags.map(tag => tag.id!);
            this.values.set(selectedIds);
          } else
          {
            // If no tags selected, set to empty array
            this.values.set([]);
          }
        });
      }
    });
  }

  areArraysEqual(arr1: number[], arr2: number[]): boolean
  {
    if (!arr1 || !arr2)
    {
      return false;
    }
    if (arr1.length !== arr2.length)
    {
      return false;
    }
    return arr1.every(value => arr2.includes(value)) && arr2.every(value => arr1.includes(value));
  }

  editValue()
  {
    this.editPressed = true;
    // If-Statement for editing every possible value
    if (this.data.property === "Version")
    {
      if (this.data.field === "Tags")
      {
        // Get the currently selected tag IDs from the values signal
        const selectedTagIds = this.values();
        if (!this.selectedProductTags.value) return;
        let updatedVersion: UpdateVersionRequestModel = {
          id: this.version.id,
          tagIds: selectedTagIds,
        }
        this.store.dispatch(new PutVersion(updatedVersion))
      }
    }

    // Close the dialog 2 seconds after editing
    this.subscribeTo(this.editedValue$, x =>
    {
      if (x) timer(2000).subscribe(() => this.dialogRef.close(true))
    });
  }
}
